import { parseEncodedObject } from './ss-utils.js';

export class BaseCard {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
  }

  clearContainer() {
    this.container.innerHTML = '';
  }

  formatPrice(price) {
    if (price === undefined || price === null) return '0.00';
    return String(price)?.split('.')?.[1]?.length === 1 ? String(price) + '0' : String(price);
  }

  getProductNameAndColor(name) {
    const [productName, productColor] = name.split(' - ');
    return {
      productName,
      productColor: productColor ? `<span class="title-color">${productColor}</span>` : ''
    };
  }

  formatImages(images, imageAlts, productName) {
    if (!images) return [];

    const imagesArr = images.split('|');
    const imageAltsArr = imageAlts || [];

    return imagesArr.map((image, index) => ({
      url: image.replace(/(\.[^.]+)$/, '_600x600$1'),
      alt: imageAltsArr[index] || productName || 'Product image'
    }));
  }

  createPriceSection(msrpPrice, price, productId) {
    if (price === undefined || price === null) return '';

    const formattedMsrp = this.formatPrice(msrpPrice);
    const formattedPrice = this.formatPrice(price);

    if (Number(formattedMsrp) <= Number(formattedPrice)) {
      return `
        <div class="price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular" data-product-id="${productId}">
            $${formattedPrice} USD
          </span>
        </div>
      `;
    }

    return `
      <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span>
          <span class="price-item price-item--regular" data-product-id="${productId}">
            $${formattedMsrp} USD
          </span>
        </span>
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
        <span class="price-item price-item--sale price-item--last" data-product-id="${productId}">
          $${formattedPrice} USD
        </span>
      </div>
    `;
  }

  createCardBadge(product, variants) {
    const isSoldOut = product.ss_available === 0 || (variants?.length > 0 && variants?.every(variant => variant.inventory_quantity <= 0));

    if (isSoldOut) {
      return `
        <span class="badge badge--bottom-left color-inverse">
          Sold Out
        </span>
      `;
    }

    if (product.ss_on_sale) {
      return `
        <span class="badge badge--bottom-left color-accent-2">
          Sale
        </span>
      `;
    }

    return '';
  }

  processVariants(variants) {
    if (!variants) return [];

    return variants.map(variant => ({
      ...variant,
      available: variant.inventory_quantity > 0
    }));
  }

  createSwatchBackground(colors) {
    if (!colors?.length) return '#FFFFFF';

    return `linear-gradient(45deg, ${colors.map((color, i) =>
      `${color} ${(i * 100) / colors.length}%, ${color} ${((i + 1) * 100) / colors.length}%`
    ).join(',')})`;
  }

  createSwatchProduct(product) {
    return {
      title: product.name,
      media: product.images.split('|').map(image => ({
        src: image,
        alt: ''
      })),
      variants: parseEncodedObject(product.variants).map(variant => ({
        ...variant,
        id: variant.id.split('/').pop(),
        options: [variant.title]
      })),
      price: Number(product.price) * 100,
      compare_at_price: Number(product.msrp) * 100,
      options: [product.name]
    };
  }

  createSwatchElement(swatch, defaultProduct) {
    if (!swatch) return '';

    const backgroundColor = swatch.swatchImages
      ? `url(${swatch.swatchImages.value[0]})`
      : swatch.swatchColors
        ? this.createSwatchBackground(JSON.parse(swatch.swatchColors.value))
        : '#FFFFFF';

    const swatchProduct = {
      id: swatch.id.split('/').pop(),
      title: swatch.title,
      media: swatch.media.map(media => ({
        src: media.image?.medium || media.sources[0].url,
        alt: swatch.title
      })),
      variants: swatch.variants.map(variant => ({
        ...variant,
        id: variant.id.split('/').pop(),
        options: [variant.title]
      })),
      price: Number(swatch.variants?.[0]?.price?.amount) * 100,
      compare_at_price: Number(swatch.variants?.[0]?.compareAtPrice?.amount || swatch.variants?.[0]?.price?.amount) * 100,
      handle: swatch.handle,
    };

    return `
      <div
        class="product-color"
        data-product='${JSON.stringify(swatchProduct)}'
        title="${swatch.title}"
        style="background: ${backgroundColor}"
      ></div>
    `;
  }

  createSwatchesSection(product) {
    if (!product.swatches?.length) return '';

    const defaultProduct = this.createSwatchProduct(product);
    const defaultBackground = this.createSwatchBackground(product.mfield_custom_colors);

    return `
      <div class="product-color-options">
        <product-color-swatches class="product-color-swatches">
          <div
            class="product-color active"
            data-product='${JSON.stringify(defaultProduct)}'
            title="${product.name}"
            style="background: ${defaultBackground}"
          ></div>
          ${product.swatches
            .sort((a, b) => a.title.localeCompare(b.title))
            .map(swatch => this.createSwatchElement(swatch, defaultProduct))
            .join('')}
        </product-color-swatches>
      </div>
    `;
  }
}